import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import VideoCard from '@/components/base/VideoCard';
import { mainCategories } from '@/mocks/categories';
import { videoItems } from '@/mocks/videos';

const typeTabs = [{ slug: 'all', name: '全部' }, ...mainCategories.map((c) => ({ slug: c.slug, name: c.name }))];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initialKeyword = params.get('q') ?? '';
  const [keyword, setKeyword] = useState(initialKeyword);
  const [activeType, setActiveType] = useState('all');
  const [submitted, setSubmitted] = useState(initialKeyword);

  const results = useMemo(() => {
    const kw = submitted.trim().toLowerCase();
    let list = videoItems;
    if (activeType !== 'all') {
      list = list.filter((v) => v.categorySlug === activeType);
    }
    if (!kw) return list;
    return list.filter((v) => {
      const haystack = [v.title, v.description, v.author, v.type, v.region, ...v.tags]
        .join(' ')
        .toLowerCase();
      return haystack.includes(kw);
    });
  }, [submitted, activeType]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSubmitted(q);
    setParams(q ? { q } : {});
  };

  const recommendations = videoItems.slice(0, 4);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6">
      <nav className="flex items-center gap-2 text-sm text-foreground-400">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-700">搜索结果</span>
      </nav>

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">搜索资源</h1>
        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="search"
              placeholder="输入影视、视频、类型或标签关键词…"
              className="w-full rounded-md border border-background-200 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-foreground-500">
            {submitted ? (
              <>
                关键词 “<strong className="text-foreground-900">{submitted}</strong>” 共匹配{' '}
                <strong className="text-primary-600">{results.length}</strong> 条内容
              </>
            ) : (
              <>
                全部资源共 <strong className="text-primary-600">{results.length}</strong> 条内容
              </>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-200/70 p-1">
            {typeTabs.map((t) => (
              <button
                key={t.slug}
                type="button"
                onClick={() => setActiveType(t.slug)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeType === t.slug
                    ? 'bg-background-50 text-primary-600 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>
      </header>

      {results.length === 0 ? (
        <div className="mt-10">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-search-eye-line text-3xl text-foreground-300" />
            <p className="mt-3 text-sm text-foreground-500">没有找到匹配的内容</p>
            <p className="mt-1 text-xs text-foreground-400">换个关键词试试，或浏览下面的相关推荐</p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setSubmitted('');
                setActiveType('all');
                setParams({});
              }}
              className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line" />
              清空搜索
            </button>
          </div>

          <section className="mt-10">
            <h2 className="mb-5 font-heading text-xl font-bold text-foreground-900">相关推荐</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {recommendations.map((item) => (
                <VideoCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {results.map((item) => (
            <VideoCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}